import type { AppConfig, ComponentPublicInstance } from 'vue'

interface ApmErrorCapture {
    captureError: (error: unknown) => void
}

const componentName = (instance: ComponentPublicInstance): string => {
    if (instance.$root === instance) return 'Root'

    return instance.$options.name || instance.$options.__name || 'Anonymous'
}

/**
 * The agent's Vue error handler, naming `<script setup>` components: apm-rum-vue's own reads only `options.name`,
 * which those never set, so it reports them all as "Anonymous". The error fields land in the error's custom context.
 */
export const createApmVueErrorHandler = (apm: ApmErrorCapture, next?: AppConfig['errorHandler']): NonNullable<AppConfig['errorHandler']> =>
    (error, instance, info) => {
        if (error && typeof error === 'object') {
            Object.assign(error, {
                ...(instance && { component: componentName(instance), file: instance.$options.__file || '' }),
                ...(info && { lifecycleHook: info }),
            })
        }

        apm.captureError(error)
        next?.(error, instance, info)
        // An app error handler stops Vue from logging the error itself.
        console.error(error)
    }
